'use client';

import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import { firebaseAuth } from '@/lib/firebase-client';
import type { ToolKind } from '@/lib/tools';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';

type ToolFavoriteButtonProps = {
  toolSlug: ToolKind;
  isFavorite: boolean;
  onChange?: (isFavorite: boolean) => void;
  onError?: (message: string) => void;
  className?: string;
  labelMode?: 'icon' | 'text';
  activeLabel?: string;
  inactiveLabel?: string;
};

function ToolFavoriteIcon({ isActive }: { isActive: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 17.2l-5.4 3.2 1.4-6.1-4.7-4.1 6.2-.5L12 4l2.5 5.7 6.2.5-4.7 4.1 1.4 6.1z"
        fill={isActive ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

export default function ToolFavoriteButton({
  toolSlug,
  isFavorite,
  onChange,
  onError,
  className,
  labelMode = 'icon',
  activeLabel = 'Saved',
  inactiveLabel = 'Save',
}: ToolFavoriteButtonProps) {
  const { user } = useFirebaseAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, setIsPending] = useState(false);

  async function handleClick() {
    if (isPending) {
      return;
    }

    if (!user) {
      router.push(`/login?next=${encodeURIComponent(pathname || `/tools/${toolSlug}`)}`);
      return;
    }

    setIsPending(true);

    try {
      const idToken = await (firebaseAuth?.currentUser ?? user).getIdToken();
      const response = await fetch('/api/tools/favorites', {
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          toolSlug,
          isFavorite: !isFavorite,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        success?: boolean;
        data?: { isFavorite?: boolean };
        error?: unknown;
      };

      if (!response.ok || !payload.success || typeof payload.data?.isFavorite !== 'boolean') {
        throw new Error(
          typeof payload.error === 'string' ? payload.error : 'Could not update saved tools.',
        );
      }

      onChange?.(payload.data.isFavorite);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Could not update saved tools right now.';
      onError?.(message);
    } finally {
      setIsPending(false);
    }
  }

  const buttonClassName = [
    labelMode === 'text'
      ? 'tool-favorite-button tool-favorite-button-text'
      : 'tool-favorite-button',
    isFavorite ? 'tool-favorite-button-active' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={buttonClassName}
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorite}
      disabled={isPending}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void handleClick();
      }}
    >
      <ToolFavoriteIcon isActive={isFavorite} />
      {labelMode === 'text' ? (
        <span>{isFavorite ? activeLabel : inactiveLabel}</span>
      ) : (
        <span className="sr-only">{isFavorite ? activeLabel : inactiveLabel}</span>
      )}
    </button>
  );
}
